/**
 * PATH: packages/ui/src/live-toasts/LiveToastCard.tsx
 * FILE_ID: UI-LIVE-TOASTS-CARD-001
 * PURPOSE: One activity toast: tone icon, title, body, meta line, up to two
 *          actions and a close button. Hover or focus pauses its timer.
 */
import { useEffect, useRef, useState } from 'react'
import { AlertTriangle, Bell, CalendarDays, CheckCircle2, Clock, Loader2, X } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '../lib/utils'
import { liveToastTtl, type LiveToast, type LiveToastAction, type LiveToastIcon, type LiveToastTone } from './liveToastStore'

const ICONS: Record<LiveToastIcon, LucideIcon> = {
  bell: Bell,
  calendar: CalendarDays,
  clock: Clock,
  check: CheckCircle2,
  alert: AlertTriangle,
}

const TONE_BADGE: Record<LiveToastTone, string> = {
  info: 'bg-primary/10 text-primary',
  success: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  warning: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  error: 'bg-red-500/15 text-red-700 dark:text-red-300',
}

const BUTTON = 'inline-flex h-8 items-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60'
const PRIMARY = 'bg-primary text-primary-foreground hover:bg-primary/90'
const SECONDARY = 'border border-border bg-background text-foreground hover:bg-muted'

export interface LiveToastCardProps {
  toast: LiveToast
  onAction: (action: LiveToastAction) => void
  onClose: () => void
}

/** Hide after the toast's lifetime unless the person is reading it. */
function useAutoHide(toast: LiveToast, paused: boolean, onClose: () => void): void {
  const ttl = liveToastTtl(toast)
  const close = useRef(onClose)
  close.current = onClose
  useEffect(() => {
    if (ttl === null || paused || toast.busy) return undefined
    const timer = window.setTimeout(() => close.current(), ttl)
    return () => window.clearTimeout(timer)
  }, [ttl, paused, toast.busy])
}

export function LiveToastCard({ toast, onAction, onClose }: LiveToastCardProps) {
  const [paused, setPaused] = useState(false)
  useAutoHide(toast, paused, onClose)
  const tone = toast.tone ?? 'info'
  const Icon = ICONS[toast.icon ?? 'bell']
  const actions = (toast.actions ?? []).slice(0, 2)

  return (
    <div
      role={tone === 'error' || tone === 'warning' ? 'alert' : 'status'}
      data-live-toast={toast.id}
      data-tone={tone}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      className="pointer-events-auto overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-lg"
    >
      <div className="flex gap-3 p-3">
        <span className={cn('mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full', TONE_BADGE[tone])}>
          <Icon className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-5">{toast.title}</p>
          {toast.body && <p className="mt-0.5 line-clamp-3 text-sm text-muted-foreground">{toast.body}</p>}
          {toast.meta && <p className="mt-1 text-xs text-muted-foreground">{toast.meta}</p>}
          {actions.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-2">
              {actions.map((action, i) => {
                const primary = (action.kind ?? (i === 0 ? 'primary' : 'secondary')) === 'primary'
                return (
                  <button
                    key={action.label}
                    type="button"
                    disabled={toast.busy}
                    onClick={() => onAction(action)}
                    className={cn(BUTTON, primary ? PRIMARY : SECONDARY)}
                  >
                    {toast.busy && primary && <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />}
                    {action.label}
                  </button>
                )
              })}
            </div>
          )}
        </div>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={onClose}
          className="-m-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
    </div>
  )
}
